import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ClipboardPaste, Loader2, X, XCircle } from "lucide-react";
import { API, formatApiError } from "../auth/api";

/**
 * 2026-10-25: "Indsæt fra Pickup". Planlæggeren kopierer kundens ordreliste fra
 * Pickup (fx ARGO) - fra siden eller mailen - og indsætter den. Kontrollen kører
 * af sig selv ved indsætning; prøven springes aldrig over (den er det eneste værn
 * mod en forkert kopi). Hver række bliver en kundebestilling, ledig (ingen
 * chauffør) - chaufførerne tager dem med "Tag turen", som de gør i Pickup i dag.
 *
 * Rot. (sandsynligvis "byt container") er et afkrydsningsfelt i Pickup, som en
 * tekstkopi mister. Kopieres der direkte fra siden, følger tabellen med som
 * HTML, og afkrydsningen læses derfra.
 */
const card = "bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50";
const input = "px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm";
const norm = (s) => String(s || "").replace(/\s+/g, " ").trim().toLowerCase();

/** Rækkerne med Rot. krydset af i den kopierede HTML - eller null, hvis det ikke kan læses. */
function rotFromHtml(html) {
  if (!html || !/<table/i.test(html)) return null;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let head = null, found = false;
    const out = [];
    doc.querySelectorAll("tr").forEach((tr) => {
      const cells = [...tr.querySelectorAll("th,td")];
      const txt = cells.map((c) => norm(c.textContent));
      if (txt.includes("#") && txt.includes("type/størrelse")) { head = txt; return; }
      if (!head) return;
      const iRot = head.findIndex((h) => h === "rot." || h === "rot");
      const iTid = head.indexOf("tid");
      if (iRot < 0 || !cells[iRot]) return;
      found = true;
      const box = cells[iRot].querySelector("input[type=checkbox]");
      if (box && (box.checked || box.hasAttribute("checked"))) out.push(`${cells[0].textContent.trim()}|${(cells[iTid]?.textContent || "").trim()}`);
    });
    return found ? out : null;
  } catch (_) { return null; }
}

export default function PickupImportDialog({ date, onClose, onDone }) {
  const [text, setText] = useState("");
  const [rot, setRot] = useState(null);
  const [day, setDay] = useState(date);
  const [res, setRes] = useState(null);
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [renaming, setRenaming] = useState(false);

  const run = async (dry, over = {}) => {
    const body = { text: over.text ?? text, rot_rows: over.rot !== undefined ? over.rot : rot, date: over.day ?? day,
      choices: over.choices ?? choices, ...(over.self_name ? { self_name: over.self_name } : {}) };
    if (!body.text.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/tours/import-pickup`, body, { params: { dry_run: dry } });
      if (dry) {
        setRes(data);
        // Kun planlæggerens egne valg huskes - om en række er valgt, følger ellers
        // kontrollen (preselect), så en række, der først fejlede, bliver valgt, når den er klar.
        setChoices((c) => {
          const next = { ...c };
          // 2026-10-27: "Husk" er til igen for pladser: et valg bliver "jeres navn", som ses og kan
          // slettes under Lokationer, og peger det på en anden plads end navnet, spørges der.
          data.rows.forEach((r) => { next[r.id] = { remember_plads: true, remember_material: true, ...(c[r.id] || {}) }; });
          return next;
        });
      } else {
        setDone(data);
        toast.success(data.message);
        onDone && onDone();
      }
    } catch (e) { toast.error(formatApiError(e)); if (dry) setRes(null); }
    finally { setBusy(false); }
  };
  const onPaste = (e) => {
    const html = e.clipboardData?.getData("text/html") || "";
    const plain = e.clipboardData?.getData("text/plain") || "";
    if (!plain) return;
    e.preventDefault();
    const r = rotFromHtml(html);
    setText(plain); setRot(r); setChoices({}); setRes(null);
    run(true, { text: plain, rot: r, choices: {} });
  };
  const choose = (id, patch, recheck = false) => {
    const next = { ...choices, [id]: { ...(choices[id] || {}), ...patch } };
    setChoices(next);
    if (recheck) run(true, { choices: next });
  };
  const picked = (res?.rows || []).filter((r) => r.ok && (choices[r.id]?.include ?? r.preselect));
  // 2026-10-27: En plads, der ikke findes, oprettes ikke af importen - ContainerFlow opretter den.
  const [asked, setAsked] = useState({});
  const askSite = async (rq) => {
    try {
      const r = (await axios.post(`${API}/logistics/master-requests`, { kind: "site", customer_id: rq.customer_id, site_name: rq.site_name })).data;
      setAsked((a) => ({ ...a, [`${rq.customer_id}|${rq.site_name}`]: true }));
      toast.success(r.duplicate ? `${rq.site_name} er allerede bedt om` : `Anmodning sendt: ContainerFlow opretter ${rq.site_name} hos ${rq.customer_name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const forget = async (kind, m) => {
    try {
      await axios.post(`${API}/tours/import-pickup/forget`, { kind, customer_id: m.customer_id, from: m.from });
      toast.success(`Glemt: "${m.from}" bruger nu sit eget navn`);
      run(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-4xl p-5 space-y-4 max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid="pickup-dialog">
        <div className="flex items-center gap-2">
          <ClipboardPaste className="w-5 h-5 text-emerald-600" />
          <div className="font-bold text-lg flex-1">Indsæt fra Pickup</div>
          <button onClick={onClose} aria-label="Luk" className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="space-y-3">
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm font-semibold">{done.message}</div>
            {done.created_master && (done.created_master.plads + done.created_master.material + done.created_master.container) > 0 && (
              <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 p-3 text-sm" data-testid="pickup-created-master">
                Oprettet hos kunden: {done.created_master.plads} {done.created_master.plads === 1 ? "plads" : "pladser"}, {done.created_master.material} {done.created_master.material === 1 ? "materiale" : "materialer"} og {done.created_master.container} {done.created_master.container === 1 ? "container" : "containere"} - de genkendes næste gang.
              </div>
            )}
            {done.created_anlaeg > 0 && (
              <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 p-3 text-sm" data-testid="pickup-created-anlaeg">
                {done.created_anlaeg} {done.created_anlaeg === 1 ? "nyt modtageanlæg" : "nye modtageanlæg"} hos kunden (fra Pickups "Til"). Udfyld adressen under Lokationer › Eksterne kunder, så chaufføren får vej og nr. - åbne ture får den med det samme.
              </div>
            )}
            {(done.errors || []).map((e) => <div key={e.row} className="text-sm text-red-700">Række {e.row}: {e.reason}</div>)}
            <div className="flex justify-end"><button className={btnPrimary} onClick={onClose}>Luk</button></div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <p className="text-sm text-slate-600 dark:text-slate-300 flex-1 min-w-[260px]">Markér hele ordrelisten i Pickup (eller i mailen), kopiér og indsæt den her. Kopierer du direkte fra Pickup-siden, læses også Rot. (byt container).</p>
              <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">Køres</label>
                <input type="date" className={input} value={day} onChange={(e) => { setDay(e.target.value); if (res) run(true, { day: e.target.value }); }} data-testid="pickup-date" /></div>
            </div>
            <textarea className={`${input} w-full font-mono text-xs h-28`} value={text} onPaste={onPaste} data-testid="pickup-text"
              onChange={(e) => { setText(e.target.value); setRot(null); }} placeholder="Indsæt listen fra Pickup her (Ctrl/Cmd + V)" />
            {!res && text.trim() && <button className={btnGhost} disabled={busy} onClick={() => run(true)} data-testid="pickup-check">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Kontrollér</button>}
            {busy && !res && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Kontrollerer ...</div>}
            {res && (
              <div className="space-y-3" data-testid="pickup-preview">
                {res.ask_self_name && (
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-sm flex flex-wrap items-center gap-2" data-testid="pickup-self">
                    <span className="flex-1">Er <b>"{res.ask_self_name}"</b> jer i Pickup? Så kan ordrer, der køres af en anden vognmand, springes over.</span>
                    <button className={btnPrimary} onClick={() => run(true, { self_name: res.ask_self_name })} data-testid="pickup-self-yes">Ja, det er os</button>
                  </div>
                )}
                {res.pickup_name && (
                  <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2" data-testid="pickup-name">
                    Jeres navn i Pickup: <b>{res.pickup_name}</b>
                    {!renaming ? <button type="button" className="underline" onClick={() => setRenaming(true)}>ret</button> : (
                      <select className={input} defaultValue="" onChange={(e) => { if (e.target.value) { setRenaming(false); run(true, { self_name: e.target.value }); } }}>
                        <option value="">Vælg jer i listen ...</option>
                        {[...new Set(res.rows.map((r) => r.carrier).filter(Boolean))].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    )}
                  </div>
                )}
                {(res.notes || []).map((n) => <div key={n} className="text-xs text-slate-600 dark:text-slate-300 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />{n}</div>)}
                <div className="divide-y divide-slate-100 dark:divide-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl">
                  {res.rows.map((r) => {
                    const ch = choices[r.id] || {};
                    return (
                      <div key={r.id} className="p-2.5 space-y-1.5" data-testid={`pickup-row-${r.row}`}>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <input type="checkbox" disabled={!r.ok} checked={!!r.ok && !!(ch.include ?? r.preselect)} onChange={(e) => choose(r.id, { include: e.target.checked })} aria-label="Opret" />
                          {r.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                          <span className="font-mono">{r.container}</span>
                          <span className="flex-1 min-w-[200px]">{r.text}</span>
                          <span className="text-xs text-slate-500">bestilt {r.ordered}{r.age_days >= 1 ? ` · for ${r.age_days} ${r.age_days === 1 ? "dag" : "dage"} siden` : ""}</span>
                          {r.ok && (
                            <select className={input} value={ch.task || r.task} onChange={(e) => choose(r.id, { task: e.target.value }, true)} aria-label="Opgave">
                              <option value="normal">Afhent container</option><option value="flyt">Byt container</option>
                            </select>
                          )}
                          {r.ok && (
                            <select className={input} value={ch.priority || "normal"} onChange={(e) => choose(r.id, { priority: e.target.value })} aria-label="Prioritet">
                              <option value="normal">Normal</option><option value="haster">Haster</option>
                            </select>
                          )}
                        </div>
                        {/* 2026-10-26 (model C): Hos en ekstern kunde (fx ARGO) oprettes det nye. */}
                        {r.new && Object.keys(r.new).length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 text-xs pl-6" data-testid={`pickup-new-${r.row}`}>
                            {r.new.plads && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">Ny plads: {r.new.plads}</span>}
                            {r.new.material && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">Nyt materiale: {r.new.material}</span>}
                            {r.new.container && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">Ny container: {r.new.container}</span>}
                            {r.new.anlaeg && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">Nyt anlæg: {r.new.anlaeg}</span>}
                            <span className="text-slate-500">oprettes, når du trykker Opret</span>
                          </div>
                        )}
                        {r.options?.plads && (
                          <div className="flex flex-wrap items-center gap-2 text-sm pl-6">
                            <span>Pladsen "{r.plads}" er:</span>
                            <select className={input} value={ch.plads || ""} onChange={(e) => choose(r.id, { plads: e.target.value }, true)} data-testid={`pickup-plads-${r.row}`}>
                              <option value="">Vælg ...</option>
                              {r.options.plads.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <label className="text-xs flex items-center gap-1" title="Navnet i Pickup bliver jeres navn for pladsen (ses og kan slettes under Lokationer)"><input type="checkbox" checked={!!ch.remember_plads} onChange={(e) => choose(r.id, { remember_plads: e.target.checked })} />Husk</label>
                            {r.suggest?.plads && !ch.plads && <button type="button" className="text-xs font-semibold underline text-emerald-700" onClick={() => choose(r.id, { plads: r.suggest.plads.value }, true)}
                              data-testid={`pickup-suggest-plads-${r.row}`}>Brug {r.suggest.plads.label}</button>}
                            {r.request && !ch.plads && (asked[`${r.request.customer_id}|${r.request.site_name}`]
                              ? <span className="text-xs text-slate-500">Bedt om - ContainerFlow opretter den</span>
                              : <button type="button" className="text-xs font-semibold underline" onClick={() => askSite(r.request)} data-testid={`pickup-request-${r.row}`}>Bed ContainerFlow oprette "{r.request.site_name}"</button>)}
                          </div>
                        )}
                        {r.options?.material && (
                          <div className="flex flex-wrap items-center gap-2 text-sm pl-6">
                            <span>Materialet "{r.material}" er:</span>
                            <select className={input} value={ch.material || ""} onChange={(e) => choose(r.id, { material: e.target.value }, true)} data-testid={`pickup-material-${r.row}`}>
                              <option value="">{r.new?.material ? `Nyt materiale "${r.new.material}"` : "Vælg ..."}</option>
                              {r.options.material.map((m) => <option key={m} value={m}>{m}</option>)}
                              {r.can_new_material && !r.new?.material && <option value="__new__">Nyt materiale "{r.material}"</option>}
                            </select>
                            {r.suggest?.material && !ch.material && <button type="button" className="text-xs font-semibold underline text-emerald-700" onClick={() => choose(r.id, { material: r.suggest.material }, true)}
                              data-testid={`pickup-suggest-material-${r.row}`}>Brug {r.suggest.material}</button>}
                            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={ch.remember_material !== false} onChange={(e) => choose(r.id, { remember_material: e.target.checked })} />Husk</label>
                          </div>
                        )}
                        {/* 2026-10-26: Et husket valg står synligt og kan glemmes (fx en plads valgt ved en fejl). */}
                        {["plads", "material"].filter((k) => r.remembered?.[k]).map((k) => (
                          <div key={k} className="flex flex-wrap items-center gap-2 text-xs pl-6 text-slate-600 dark:text-slate-300" data-testid={`pickup-remembered-${k}-${r.row}`}>
                            <span>Husket: "{r.remembered[k].from}" = <b>{r.remembered[k].to}</b></span>
                            <button type="button" className="underline font-semibold" onClick={() => forget(k, r.remembered[k])}>Glem</button>
                          </div>
                        ))}
                        {r.errors.map((e) => <div key={e} className="text-xs text-red-700 pl-6">{e}</div>)}
                        {r.warnings.map((w) => <div key={w} className="text-xs text-amber-700 dark:text-amber-300 pl-6">{w}</div>)}
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <span className="text-xs text-slate-500 flex-1">{res.valid} klar · {res.invalid} med fejl (oprettes ikke). Bestillingerne er ledige - chaufførerne tager dem med "Tag turen".</span>
                  <button className={btnGhost} onClick={onClose}>Annuller</button>
                  <button className={btnPrimary} disabled={busy || !picked.length} onClick={() => run(false)} data-testid="pickup-create">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}Opret {picked.length} {picked.length === 1 ? "bestilling" : "bestillinger"}</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>, document.body);
}
